import { Link, useNavigate, useParams } from "react-router-dom";
import { useToasts } from "../../contexts/toasts";
import { useCallback, useEffect, useState } from "react";
import { Facility } from "../../types/Structure";
import { Facilities } from "../../api/Facilities";
import { Breadcrumb, Card, Col, Container, Row } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import { Alert, UploadProps } from "antd";
import { TailSpin } from "react-loader-spinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen } from "@fortawesome/free-solid-svg-icons";
import { Sports } from "../../api/Sports";
import { Option } from "../../models/generics";
import { getSportName } from "../../utils/stringUtil";
import { FacilityType } from "../../enums/FacilityType";
import "../../Components/list-items/BookingItem.css";
import "./FacilityEdit.css";

const weekDays = [
  { label: "Dom", value: 0 },
  { label: "Lun", value: 1 },
  { label: "Mar", value: 2 },
  { label: "Mer", value: 3 },
  { label: "Gio", value: 4 },
  { label: "Ven", value: 5 },
  { label: "Sab", value: 6 },
];

export default function FacilityDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToasts();

  const [sportOptions, setSportOptions] = useState<Option[]>([]);
  const [facility, setFacility] = useState<Facility>();
  const [fileList, setFileList] = useState<UploadProps["fileList"]>([]);
  const [error, setError] = useState(false);

  const initSports = useCallback(() => {
    Sports.getSports({}).then(
      ({ data }) => {
        setSportOptions(
          data.list.map((sport) => ({
            label: getSportName(sport.name),
            value: sport.id,
          }))
        );
      },
      () => {
        setSportOptions([]);
        showToast("sports-error", "Errore nel reperimento degli Sport", "", "danger");
      }
    );
  }, [showToast]);

  const retrieveFacility = useCallback(() => {
    Facilities.getFacilityById(id!).then(
      async ({ data }) => {
        const fac = data.facility as Facility;
        if (fac.facilityImageUrl) {
          try {
            const { data: imageData } = await Facilities.getFacilityImageUrl(fac.id!);
            setFileList([
              {
                uid: fac.id!,
                name: imageData.url.split("/")[4].split("?")[0],
                status: "done",
                url: imageData.url,
              },
            ]);
          } catch {
            showToast("facility-image-error", "Errore nel reperimento del Logo del campo", "", "danger");
          }
        }
        setFacility(fac);
      },
      () => {
        setError(true);
        showToast("facility-error", "Errore nel reperimento del Campo", "", "danger");
      }
    );
  }, [id, showToast]);

  useEffect(() => {
    initSports();
    if (id) retrieveFacility();
    else setFacility(new Facility());
  }, [id, initSports, retrieveFacility]);

  const sportName = sportOptions.find((s) => s.value === facility?.sportId)?.label ?? "—";

  const renderContent = useCallback(() => {
    if (error)
      return (
        <Alert
          type="error"
          showIcon
          message="Si è verificato un errore nel reperimento dei dati sul Campo. Riprova più tardi."
          className="m-3"
        />
      );

    if (!facility)
      return (
        <div className="d-flex justify-content-center py-5 my-5">
          <TailSpin height={100} width={100} color="#f25d0b" radius="1" />
        </div>
      );

    return (
      <div className="fd-container">
        <Row className="g-3">

          {/* ── Colonna sinistra: informazioni ── */}
          <Col xs={12} md={5}>
            <div className="fd-section h-100">
              <div className="fd-section-title">Informazioni</div>

              <div className="fd-info-row">
                <span className="fd-label">Nome</span>
                <span className="fd-value">{facility.name ?? "—"}</span>
              </div>

              <div className="fd-info-row">
                <span className="fd-label">Sport</span>
                <span className="fd-value">{sportName}</span>
              </div>

              <div className="fd-info-row">
                <span className="fd-label">Tipologia</span>
                <span className="fd-value">
                  {facility.type === FacilityType.Indoor ? "Indoor" : "Outdoor"}
                </span>
              </div>

              {fileList?.[0]?.url && (
                <div className="fd-info-row">
                  <span className="fd-label">Logo</span>
                  <img
                    src={fileList[0].url}
                    alt="Logo campo"
                    height={36}
                    style={{ borderRadius: 6, objectFit: "contain" }}
                  />
                </div>
              )}
            </div>
          </Col>

          {/* ── Colonna destra: fasce orarie ── */}
          <Col xs={12} md={7}>
            <div className="fd-section h-100">
              <div className="fd-section-title">Fasce Orarie</div>
              <div className="d-flex flex-column gap-2">
                {weekDays.map((day) => {
                  const group = facility.timeSlots?.find(
                    (g) => g.dayOfWeek === day.value
                  );
                  const isEnabled = group?.enabled ?? false;
                  const slots = group?.slots ?? [];

                  return (
                    <div
                      key={day.value}
                      className={`fd-day-card ${!isEnabled ? "fd-day-disabled" : ""}`}
                    >
                      <span className="fd-day-name">{day.label}</span>
                      {isEnabled && slots.length > 0 ? (
                        <div className="fd-slots-row">
                          {slots.map((slot, i) => (
                            <span key={i} className="fd-slot-chip">
                              {slot.time}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="fd-no-slots">
                          {isEnabled ? "Nessuna fascia" : "Chiuso"}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </Col>

        </Row>
      </div>
    );
  }, [error, facility, sportName, fileList]);

  return (
    <Container className="my-3">
      <Helmet>
        <title>{facility?.name || "Dettaglio Campo"}</title>
      </Helmet>

      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/gestione-campi" }}>
          Gestione Campi
        </Breadcrumb.Item>
        <Breadcrumb.Item active>{facility?.name || "..."}</Breadcrumb.Item>
      </Breadcrumb>

      <Card>
        <div className="fd-page-header">
          <div>
            <p className="fd-page-title">{facility?.name ?? "Campo"}</p>
            <p className="fd-page-subtitle">{sportName}</p>
          </div>
          <button
            className="circle-button p-3"
            disabled={!facility}
            onClick={() => navigate("modifica")}
          >
            <FontAwesomeIcon icon={faPen} />
          </button>
        </div>

        {renderContent()}
      </Card>
    </Container>
  );
}
